import { useRef, useState } from 'react';
import { X, Upload } from '../../icons';
import { Button } from '../../components/shared';
import { uploadWorkspaceFile, createFolder, formatBytes, type QuotaExceededBody } from '../../api/workspace';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import { useEscapeClose } from '../../hooks/useEscapeClose';
import { tWorkspace } from '../../lib/workspace-i18n';
import { planFolderUpload, parentPathOf, leafNameOf } from '../../lib/workspace-folder-upload';

/** 서버(workspace-upload.routes.ts)의 WORKSPACE_MAX_SIZE_BYTES 와 같은 값. */
const MAX_SIZE_BYTES = 200 * 1024 * 1024;

interface FileUploadModalProps {
  parentId: string | null;
  parentName: string;
  onClose: () => void;
  onUploaded: () => void;
  isLeaving?: boolean;
  /**
   * 폴더 통째로 올리기 (2026-08-14 additive). 미지정 시 지금까지와 동일한 파일 업로드.
   *
   * 켜면 `<input webkitdirectory>` 로 바뀌고, 업로드 **전에** 각 파일의
   * `webkitRelativePath` 로 폴더를 먼저 만든다 — 안 하면 중첩 구조가 통째로 사라지고
   * 파일이 한 폴더에 쏟아진다.
   */
  directory?: boolean;
  /**
   * 미리 채워 둘 파일 (2026-08-19 additive) — 목록에 끌어다 놓고 여는 경로.
   * 없으면 지금까지처럼 사용자가 직접 고른다.
   */
  initialFiles?: readonly File[];
}

interface RowState {
  file: File;
  percent: number;
  status: 'pending' | 'uploading' | 'done' | 'error';
  error?: string;
}

/**
 * FileUploadModal (2026-08-12) — 워크스페이스 파일 업로드.
 *
 * 형식은 FolderCreateModal 을 따른다(같은 backdrop/modal 클래스·focus trap·Esc 닫기).
 * 업로드 자체는 api/workspace 의 uploadWorkspaceFile 이 presign → PUT → finalize 3단계를
 * 처리하며, PUT/finalize 헬퍼는 채팅 첨부와 같은 것을 재사용한다.
 *
 * 여러 개를 고르면 **순차** 처리한다. 병렬로 올리면 쿼터 검사가 서로의 사용량을 보지 못해
 * 합계가 한도를 넘겨 통과할 수 있다(각 presign 이 자기 시점의 사용량만 본다).
 */
export function FileUploadModal({
  parentId, parentName, onClose, onUploaded, isLeaving = false, directory = false, initialFiles,
}: FileUploadModalProps) {
  const trapRef = useFocusTrap<HTMLDivElement>(true);
  const inputRef = useRef<HTMLInputElement>(null);
  const [rows, setRows] = useState<RowState[]>(
    () => (initialFiles ?? []).map((file) => ({ file, percent: 0, status: 'pending' as const })),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadedCount, setUploadedCount] = useState(0);
  /** 폴더 만드는 동안의 진행 안내 — 파일이 많으면 이 단계만으로도 몇 초 걸린다. */
  const [folderProgress, setFolderProgress] = useState<string | null>(null);

  useEscapeClose(onClose, busy);

  /**
   * 폴더 구조를 먼저 만들고 "경로 → folderId" 표를 돌려준다.
   *
   * 얕은 경로부터 순서대로 만든다 — 부모가 있어야 자식의 parentId 를 알 수 있다.
   * 하나라도 실패하면 그 아래는 전부 갈 곳이 없으므로 즉시 멈춘다(반쯤 만들어진 트리에
   * 파일을 흩뿌리지 않는다).
   */
  async function ensureFolders(files: File[]): Promise<Map<string, string | null>> {
    const plan = planFolderUpload(files);
    const idByPath = new Map<string, string | null>([['', parentId]]);
    for (let i = 0; i < plan.folders.length; i += 1) {
      const path = plan.folders[i]!;
      setFolderProgress(tWorkspace('workspace.folder_upload.creating', {
        done: i + 1, total: plan.folders.length,
      }));
      const parentOfThis = idByPath.get(parentPathOf(path));
      const created = await createFolder({
        name: leafNameOf(path),
        parentId: parentOfThis ?? null,
      });
      idByPath.set(path, created.id);
    }
    setFolderProgress(null);
    return idByPath;
  }

  function handlePick(e: React.ChangeEvent<HTMLInputElement>) {
    const picked = Array.from(e.target.files ?? []);
    setRows(picked.map((file) => ({ file, percent: 0, status: 'pending' as const })));
    setError(null);
    setUploadedCount(0);
  }

  function patchRow(index: number, patch: Partial<RowState>) {
    setRows((cur) => cur.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (rows.length === 0) {
      setError(tWorkspace('workspace.upload.empty_pick'));
      return;
    }
    setBusy(true);
    setError(null);
    let ok = 0;
    /**
     * 실패한 파일 수 — **닫을지 말지를 이 값이 정한다** (2026-08-14).
     * rows 상태로 세지 않는 이유: `patchRow` 는 setState 라 이 루프 안에서는 아직 반영되지
     * 않는다. 화면 상태를 읽어 판단하면 방금 실패한 파일을 못 보고 닫아 버린다.
     */
    let failed = 0;

    // 폴더 업로드면 구조부터 만든다. 실패하면 파일은 손도 대지 않는다.
    let folderIds: Map<string, string | null> | null = null;
    if (directory) {
      try {
        folderIds = await ensureFolders(rows.map((r) => r.file));
      } catch (err) {
        setFolderProgress(null);
        setError((err as any)?.response?.data?.message ?? (err as Error).message);
        setBusy(false);
        return;
      }
    }
    const plan = directory ? planFolderUpload(rows.map((r) => r.file)) : null;

    for (let i = 0; i < rows.length; i += 1) {
      const row = rows[i]!;
      if (row.status === 'done') continue;

      // 서버도 스키마로 막지만, 큰 파일을 다 올린 뒤 거절당하는 낭비를 먼저 차단한다.
      if (row.file.size > MAX_SIZE_BYTES) {
        patchRow(i, { status: 'error', error: tWorkspace('workspace.upload.err.too_large', { name: row.file.name }) });
        failed += 1;
        continue;
      }

      patchRow(i, { status: 'uploading', percent: 0 });
      try {
        // 폴더 업로드면 방금 만든 하위 폴더로, 아니면 지금 폴더로.
        const target = plan ? (folderIds?.get(plan.fileDir[i] ?? '') ?? parentId) : parentId;
        await uploadWorkspaceFile(row.file, target, (percent) => patchRow(i, { percent }));
        patchRow(i, { status: 'done', percent: 100 });
        ok += 1;
      } catch (err) {
        const res = (err as { response?: { status?: number; data?: QuotaExceededBody } })?.response;
        if (res?.status === 413 && res.data?.code === 'QUOTA_EXCEEDED') {
          const q = res.data;
          patchRow(i, {
            status: 'error',
            error: `${tWorkspace('workspace.upload.err.quota')} (${formatBytes(q.usedBytes)} / ${
              q.limitBytes < 0 ? '∞' : formatBytes(q.limitBytes)})`,
          });
          // 한도를 넘은 시점부터 남은 파일도 전부 실패한다 — 계속 시도하지 않고 멈춘다.
          setError(tWorkspace('workspace.upload.err.quota'));
          failed += 1;
          break;
        }
        const reason = (err as any)?.response?.data?.message ?? (err as Error).message;
        patchRow(i, { status: 'error', error: tWorkspace('workspace.upload.err.failed', { name: row.file.name, reason }) });
        failed += 1;
      }
    }

    setUploadedCount(ok);
    setBusy(false);
    if (ok > 0) onUploaded();

    /**
     * 전부 성공했으면 **스스로 닫는다** (2026-08-14 — 사용자 보고 "완료돼도 팝업이 살아 있어
     * 취소를 눌러야 한다").
     *
     * ⚠ 실패가 하나라도 있으면 **열어 둔다.** 목록에 어떤 파일이 왜 실패했는지가 이 모달
     *   안에만 있어서, 닫으면 사용자는 몇 개가 올라갔는지조차 알 수 없다.
     *   (쿼터 초과로 중간에 멈춘 경우도 failed 가 서 있어 여기 걸린다.)
     * 성공 안내(`uploadedCount`)는 그 부분 실패 상황에서만 보이게 된다 — 전부 성공했을 때의
     * 피드백은 **닫힌 뒤 목록에 파일이 나타나는 것** 자체다.
     */
    if (failed === 0 && ok > 0) onClose();
  }

  return (
    <div
      className={`workspace-modal-backdrop${isLeaving ? ' workspace-modal-backdrop--leaving' : ''}`}
      onClick={(e) => { if (e.target === e.currentTarget && !busy && !isLeaving) onClose(); }}
      role="dialog"
      aria-modal="true"
      aria-label={tWorkspace('workspace.upload.aria_dialog')}
      aria-hidden={isLeaving || undefined}
    >
      <div ref={trapRef} className={`workspace-modal${isLeaving ? ' workspace-modal--leaving' : ''}`}>
        <header className="workspace-modal-header">
          <h2>{tWorkspace(directory ? 'workspace.folder_upload.heading' : 'workspace.upload.heading')}</h2>
          <Button variant="ghost" size="sm" iconOnly
            onClick={onClose} disabled={busy} aria-label={tWorkspace('workspace.modal.aria_close')}
            iconLeft={<X size={18} />}
          />
        </header>

        <form className="workspace-modal-body" onSubmit={handleSubmit}>
          <div className="workspace-modal-context">
            {tWorkspace('workspace.upload.hint_parent')}: <strong>{parentName}</strong>
          </div>

          <div className="workspace-modal-field">
            <label htmlFor="workspace-upload-input">
              {tWorkspace(directory ? 'workspace.folder_upload.label_pick' : 'workspace.upload.label_pick')}
            </label>
            {/*
              ⚠ webkitdirectory 는 표준 속성이 아니라 React 타입에 없다. 소문자 그대로
                넘겨야 DOM 에 붙는다(camelCase 로 쓰면 React 가 무시한다).
            */}
            <input
              id="workspace-upload-input"
              ref={inputRef}
              type="file"
              multiple
              disabled={busy}
              onChange={handlePick}
              {...(directory
                ? ({ webkitdirectory: '', directory: '' } as unknown as Record<string, string>)
                : {})}
            />
            <p className="workspace-upload-hint">
              {tWorkspace(directory ? 'workspace.folder_upload.hint' : 'workspace.upload.hint_max')}
            </p>
          </div>

          {folderProgress && (
            <div className="workspace-upload-done" role="status">{folderProgress}</div>
          )}

          {rows.length > 0 && (
            <ul className="workspace-upload-list">
              {rows.map((r, i) => (
                <li key={`${r.file.name}-${i}`} className={`workspace-upload-row workspace-upload-row--${r.status}`}>
                  <span className="workspace-upload-name" title={r.file.name}>{r.file.name}</span>
                  <span className="workspace-upload-size">{formatBytes(r.file.size)}</span>
                  <span className="workspace-upload-state">
                    {r.status === 'uploading' && `${r.percent}%`}
                    {r.status === 'done' && '✓'}
                    {r.status === 'error' && '!'}
                  </span>
                  {r.error && <span className="workspace-upload-error">{r.error}</span>}
                </li>
              ))}
            </ul>
          )}

          {error && <div className="workspace-modal-error" role="alert">{error}</div>}
          {uploadedCount > 0 && !busy && (
            <div className="workspace-upload-done" role="status">
              {tWorkspace('workspace.upload.result_ok', { count: uploadedCount })}
            </div>
          )}

          <footer className="workspace-modal-footer">
            <Button variant="secondary"
              onClick={onClose} disabled={busy}
            >
              {tWorkspace('workspace.modal.btn_cancel')}
            </Button>
            <Button variant="primary" type="submit"
              disabled={busy || rows.length === 0}
              iconLeft={<Upload size={14} aria-hidden />}
            >
              {busy ? tWorkspace('workspace.upload.btn_loading') : tWorkspace('workspace.upload.btn_idle')}
            </Button>
          </footer>
        </form>
      </div>
    </div>
  );
}
